import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '@/api/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useCompare } from '@/contexts/CompareContext';
import { toast } from 'sonner';
import { Search, Filter, X, Plus, Check, Shield, Heart, Car, Plane, ArrowRight, CheckCircle, XCircle, TrendingUp } from 'lucide-react';

const PoliciesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [policies, setPolicies] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || 'all');
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [showFilters, setShowFilters] = useState(false);

  const { addToCompare, isInCompare, compareList } = useCompare();

  const insuranceTypes = [
    { value: 'all', label: 'All Types', icon: Shield },
    { value: 'Health', label: 'Health', icon: Shield },
    { value: 'Life', label: 'Life', icon: Heart },
    { value: 'Motor', label: 'Motor', icon: Car },
    { value: 'Travel', label: 'Travel', icon: Plane }
  ];

  useEffect(() => {
    fetchPolicies();
    fetchProviders();
  }, [selectedType, selectedProvider, priceRange, search]);

  const fetchPolicies = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedType && selectedType !== 'all') params.type = selectedType;
      if (selectedProvider && selectedProvider !== 'all') params.provider_id = selectedProvider;
      if (priceRange[0] > 0) params.min_premium = priceRange[0];
      if (priceRange[1] < 100000) params.max_premium = priceRange[1];
      if (search) params.q = search;

      const response = await api.get('/policies', { params });
      setPolicies(response.data);
    } catch (error) {
      console.error('Failed to fetch policies:', error);
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await api.get('/providers');
      setProviders(response.data);
    } catch (error) {
      console.error('Failed to fetch providers:', error);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchPolicies();
  };

  const clearFilters = () => {
    setSelectedType('all');
    setSelectedProvider('all');
    setPriceRange([0, 100000]);
    setSearch('');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-secondary-50" data-testid="policies-page">
      {/* Header */}
      <div className="bg-white border-b border-secondary-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8">
          <h1 className="text-3xl sm:text-4xl font-bold font-heading text-secondary-900 mb-2">
            Compare Insurance Policies
          </h1>
          <p className="text-lg text-secondary-600 mb-6">
            Find and compare the best policies from top providers
          </p>

          {/* Trust Badges */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
              <CheckCircle className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-secondary-700">Instant Comparison</span>
            </div>
            <div className="flex items-center gap-2 bg-green-50 px-4 py-2 rounded-lg">
              <Shield className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-secondary-700">Trusted Providers</span>
            </div>
            <div className="flex items-center gap-2 bg-purple-50 px-4 py-2 rounded-lg">
              <TrendingUp className="h-5 w-5 text-purple-600" />
              <span className="text-sm font-medium text-secondary-700">Best Prices</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className={`lg:block ${showFilters ? 'block' : 'hidden'}`}>
            <Card className="sticky top-4">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl font-semibold">Filters</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    data-testid="clear-filters-button"
                  >
                    Clear All
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Search */}
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-2 block">
                    Search
                  </label>
                  <form onSubmit={handleSearchSubmit}>
                    <Input
                      placeholder="Search policies..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      data-testid="search-policies-input"
                    />
                  </form>
                </div>

                {/* Insurance Type */}
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-2 block">
                    Insurance Type
                  </label>
                  <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger data-testid="insurance-type-filter">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {insuranceTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Provider */}
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-2 block">
                    Provider
                  </label>
                  <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                    <SelectTrigger data-testid="provider-filter">
                      <SelectValue placeholder="All providers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Providers</SelectItem>
                      {providers.map((provider) => (
                        <SelectItem key={provider.id} value={provider.id}>
                          {provider.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Price Range */}
                <div>
                  <label className="text-sm font-medium text-secondary-700 mb-2 block">
                    Annual Premium Range
                  </label>
                  <div className="space-y-4">
                    <Slider
                      min={0}
                      max={100000}
                      step={1000}
                      value={priceRange}
                      onValueChange={setPriceRange}
                      data-testid="price-range-slider"
                    />
                    <div className="flex items-center justify-between text-sm text-secondary-600">
                      <span>{formatCurrency(priceRange[0])}</span>
                      <span>{formatCurrency(priceRange[1])}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Policies Grid */}
          <div className="lg:col-span-3">
            {/* Mobile Filter Toggle */}
            <div className="lg:hidden mb-4">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters-button"
              >
                <Filter className="mr-2 h-4 w-4" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </div>

            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-secondary-600" data-testid="results-count">
                {loading ? 'Loading...' : `${policies.length} policies found`}
              </p>
              {compareList.length > 0 && (
                <Link to="/compare">
                  <Button variant="default" size="sm" data-testid="view-comparison-button">
                    View Comparison ({compareList.length})
                  </Button>
                </Link>
              )}
            </div>

            {/* Policies Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <Card key={i} className="border-2 border-secondary-200">
                    <CardHeader>
                      <Skeleton className="h-4 w-20 mb-3" />
                      <Skeleton className="h-6 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/2" />
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <Skeleton className="h-20 w-full rounded-lg" />
                      <Skeleton className="h-16 w-full" />
                      <div className="space-y-2">
                        <Skeleton className="h-8 w-full" />
                        <Skeleton className="h-8 w-full" />
                      </div>
                    </CardContent>
                    <CardFooter className="flex-col gap-2 bg-secondary-50">
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-10 w-full" />
                    </CardFooter>
                  </Card>
                ))}
              </div>
            ) : policies.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Search className="h-16 w-16 text-secondary-300 mb-4" />
                  <h3 className="text-xl font-semibold text-secondary-900 mb-2">No policies found</h3>
                  <p className="text-base text-secondary-600 mb-6">Try adjusting your filters</p>
                  <Button onClick={clearFilters} data-testid="clear-all-filters-button">
                    Clear Filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {policies.map((policy) => (
                  <Card
                    key={policy.id}
                    className="hover:shadow-xl transition-all duration-200 border-2 border-secondary-200 hover:border-primary-400 relative overflow-hidden"
                    data-testid={`policy-card-${policy.id}`}
                  >
                    {/* Badge Ribbon */}
                    <div className="absolute top-0 right-0">
                      {policy.is_popular && (
                        <div className="bg-green-500 text-white text-xs px-3 py-1 rounded-bl-lg font-semibold">
                          Popular
                        </div>
                      )}
                    </div>

                    <CardHeader className="pb-4">
                      <Badge variant="secondary" className="text-xs w-fit mb-3">
                        {policy.category_type}
                      </Badge>
                      <CardTitle className="text-xl font-semibold text-secondary-900 mb-1">
                        {policy.name}
                      </CardTitle>
                      <CardDescription className="text-sm text-secondary-600 font-medium">
                        {policy.provider_name}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-4 pb-4">
                      {/* Coverage Amount */}
                      <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
                        <p className="text-xs text-secondary-600 mb-1">Cover Amount</p>
                        <p className="text-2xl font-bold font-mono text-primary-700">
                          {formatCurrency(policy.coverage_amount)}
                        </p>
                      </div>

                      {/* Monthly Premium */}
                      <div>
                        <p className="text-xs text-secondary-600 mb-1">Starting at</p>
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-bold font-mono text-secondary-900">
                            {formatCurrency(policy.premium_yearly / 12)}
                          </span>
                          <span className="text-base text-secondary-600">/month</span>
                        </div>
                        <p className="text-xs text-secondary-500 mt-1">
                          ({formatCurrency(policy.premium_yearly)}/year)
                        </p>
                      </div>

                      {/* Features */}
                      <div className="pt-2 space-y-2">
                        <div className="flex items-center justify-between text-sm py-2 border-t border-secondary-200">
                          <span className="text-secondary-600">Cashless Facility</span>
                          {policy.features.cashless ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          ) : (
                            <XCircle className="h-5 w-5 text-secondary-300" />
                          )}
                        </div>
                        <div className="flex items-center justify-between text-sm py-2 border-t border-secondary-200">
                          <span className="text-secondary-600">Pre-existing Cover</span>
                          {policy.features.pre_existing ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          ) : (
                            <XCircle className="h-5 w-5 text-secondary-300" />
                          )}
                        </div>
                        {policy.features.network_size && (
                          <div className="flex items-center justify-between text-sm py-2 border-t border-secondary-200">
                            <span className="text-secondary-600">Network Size</span>
                            <span className="font-semibold text-secondary-900">
                              {policy.features.network_size.toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>

                    <CardFooter className="flex-col gap-2 pt-4 border-t border-secondary-200 bg-secondary-50">
                      <Link to={`/policy/${policy.id}`} className="w-full">
                        <Button variant="default" className="w-full bg-primary-600 hover:bg-primary-700" data-testid={`view-details-${policy.id}`}>
                          View More Features
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                      <Button
                        variant={isInCompare(policy.id) ? 'secondary' : 'outline'}
                        className="w-full"
                        onClick={() => addToCompare(policy)}
                        disabled={isInCompare(policy.id)}
                        data-testid={`add-to-compare-${policy.id}`}
                      >
                        {isInCompare(policy.id) ? (
                          <>
                            <Check className="mr-2 h-4 w-4" />
                            Added to Compare
                          </>
                        ) : (
                          <>
                            <Plus className="mr-2 h-4 w-4" />
                            Add to Compare
                          </>
                        )}
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PoliciesPage;
